import * as React from "react";
import * as moment from "moment";
import {none, Option, option} from "ts-option";
import {RouteComponentProps, withRouter} from "react-router";
import {api} from "../../api/api";
import {CreatedEvChargingJob, EvChargingBillCheck, EvChargingBillMessage, EvChargingMeterPoint} from "../../api/EvChargingApi";
import {PartnerBrandSearch} from "../../model/partnerBrand/PartnerBrand";
import PartnerBrandSelectionComponent from "../partnerBrand/search/PartnerBrandSelectionComponent";
import PageHeaderView from "../body/PageHeaderView";
import PageContentView from "../body/PageContentView";
import SectionView from "../layout/section/SectionView";
import Row from "../layout/Row";
import Col from "../layout/Col";
import FormButtonsView from "../ui/form/FormButtonsView";
import FormGroup from "../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonSecondary from "../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import {Boxed} from "../../hiberUiKit/src/molecules/Boxed/Boxed";
import Routes from "../../navigation/route/Route";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import BillPreview, {EvidenceFile} from "./BillPreview";
import MprnField from "./MprnField";
import EvChargingDateField from "./EvChargingDateField";
import NewMeterConfirm from "./NewMeterConfirm";
import {evColors, evMessageStyles} from "./evChargingStyles";
import {
	formatCredits,
	formatDate,
	formatKwh,
	formatMprn,
	ISO_DATE,
	MPRN_LENGTH,
	messagesFor,
	nextPeriodFrom,
	parseKwh,
	periodDays,
	quarterLabel
} from "./evChargingBill";

/**
 * How long typing has to pause before the bill is checked again.
 */
const CHECK_DELAY_MS = 400;

const MAX_FILES = 20;

/**
 * Files uploaded at once: each is two requests, so one at a time made fifteen screenshots a long wait.
 */
const PARALLEL_UPLOADS = 4;

interface Saved {
	job: CreatedEvChargingJob;
	partnerBrandName: string;
	mprn: string;
	periodFrom: string;
	periodTo: string;
	kwh: number;
	fileNames: string[];
}

interface CheckedInput {
	mprn: string;
	periodFrom?: string;
	periodTo?: string;
	kwh: string;
}

interface State {
	partnerBrand: Option<PartnerBrandSearch>;
	meterPoints: EvChargingMeterPoint[];
	meterPointsLoading: boolean;
	mprn: string;
	/**
	 * The person confirmed that a number no meter has yet is a new meter, not a typo.
	 */
	newMeterConfirmed: boolean;
	periodFrom: Option<moment.Moment>;
	periodTo: Option<moment.Moment>;
	kwh: string;
	files: EvidenceFile[];
	/**
	 * The file shown in the preview.
	 */
	selectedFileKey?: string;
	check?: EvChargingBillCheck;
	/**
	 * What the check on screen was made for. A message stays on its field while that field still reads the same,
	 * so typing elsewhere neither blinks it away nor leaves one about an older value.
	 */
	checkedInput?: CheckedInput;
	/**
	 * The bill changed since the check on screen was made; saving waits for the new one.
	 */
	checking: boolean;
	checkFailed: boolean;
	saving: boolean;
	saveError?: string;
	saved?: Saved;
	/**
	 * Bumped to remount the date inputs, which keep their own text and would not clear otherwise.
	 */
	resetKey: number;
}

const initialState: State = {
	partnerBrand: none,
	meterPoints: [],
	meterPointsLoading: false,
	mprn: "",
	newMeterConfirmed: false,
	periodFrom: none,
	periodTo: none,
	kwh: "",
	// listed although empty, so that resetting to this state clears them rather than leaving them as they were
	files: [],
	selectedFileKey: undefined,
	check: undefined,
	checkedInput: undefined,
	checking: false,
	checkFailed: false,
	saving: false,
	saveError: undefined,
	saved: undefined,
	resetKey: 0
};

type Props = RouteComponentProps<{}>;

/**
 * One electricity bill of a meter dedicated to EV charging, entered by the back office. The partner brand comes first
 * because it narrows the meters to the ones that make sense; the bill is checked as it is typed, by the same rules the
 * save applies, so a problem shows on its field before anyone presses Create. The job goes against the brand's own
 * default contact: a bill names nobody at the hotel worth choosing.
 */
class EvChargingBillPage extends React.Component<Props, State> {
	state: State = initialState;

	private checkTimer?: number;
	private checkSequence = 0;
	private checkFailureReported = false;
	private savedActions: HTMLDivElement | null = null;
	private periodToField: HTMLDivElement | null = null;
	private fileKeys = 0;

	componentWillUnmount() {
		window.clearTimeout(this.checkTimer);
		// stops answers still in flight from landing on an unmounted page
		this.checkSequence++;
		releaseFiles(this.state.files);
	}

	render() {
		return (
			<Boxed>
				<PageHeaderView header={"New EV charging job"}/>
				<PageContentView>
					{this.state.saved ? this.renderSaved(this.state.saved) : this.renderForm()}
				</PageContentView>
			</Boxed>
		);
	}

	private renderForm() {
		const problems = this.shownProblems();
		return (
			<Row>
				<Col xl={6} lg={6}>
					<SectionView>
						<BillPreview files={this.state.files} selectedKey={this.state.selectedFileKey}
									 onFilesAdded={this.onFilesAdded} onFileRemoved={this.onFileRemoved}
									 onFileSelected={selectedFileKey => this.setState({selectedFileKey})}/>
					</SectionView>
				</Col>
				<Col xl={6} lg={6}>
					<SectionView>
						<div onKeyDown={this.submitOnEnter}>
							{this.renderFields(problems)}
							{this.renderSummary(problems)}
						</div>
					</SectionView>
				</Col>
			</Row>
		);
	}

	private renderFields(problems: EvChargingBillMessage[]) {
		const {partnerBrand, meterPoints, meterPointsLoading, mprn, check, newMeterConfirmed, periodFrom, periodTo, kwh, resetKey} = this.state;
		const existing = meterPoints.filter(m => m.mprn === mprn)[0];
		const validFrom = periodFrom.filter(m => m.isValid()).orNull || undefined;
		const parsedKwh = parseKwh(kwh);
		const period = this.periodMessage(problems, existing);
		return (
			<>
				<FormGroup label={"Partner brand"}>
					<PartnerBrandSelectionComponent selectedValue={partnerBrand} onSelect={this.onPartnerBrandSelected}
													placeholder={"Type to search partner brands"} clearable/>
				</FormGroup>
				<div className={"m-form-group"}>
					<label className={"m-form-group_label"} htmlFor={"ev-mprn"}>MPRN</label>
					<MprnField id={"ev-mprn"} digits={mprn} meterPoints={meterPoints} meterPointsLoading={meterPointsLoading}
							   disabled={partnerBrand.isEmpty} invalid={messagesFor("mprn", problems).length > 0}
							   describedBy={"ev-mprn-messages"}
							   onChange={digits => this.update({mprn: digits, newMeterConfirmed: false})}/>
					<div id={"ev-mprn-messages"} aria-live={"polite"}>
						{this.renderMessages("mprn", problems, "error")}
						{existing &&
						<div style={evMessageStyles.hint}>
							Existing meter{existing.nickname ? ` · ${existing.nickname}` : ""} ·{" "}
							{existing.billCount} {existing.billCount === 1 ? "job" : "jobs"}
							{existing.lastBillPeriodTo ? `, the last to ${formatDate(existing.lastBillPeriodTo)}` : ""}
						</div>}
						{this.createsMeterPoint() &&
						<NewMeterConfirm digits={mprn} similarMeterPoints={check!.similarMeterPoints}
										 confirmed={newMeterConfirmed}
										 onUseExisting={digits => this.update({mprn: digits, newMeterConfirmed: false})}
										 onConfirmedChange={confirmed => this.setState({newMeterConfirmed: confirmed})}/>}
					</div>
				</div>
				<Row>
					<Col xl={6} lg={6} md={6}>
						<div className={"m-form-group"} style={styles.dateGroup}>
							<label style={{display: "block"}}>
								<span className={"m-form-group_label"}>Period from</span>
								<EvChargingDateField key={`from-${resetKey}`} value={periodFrom}
													 invalid={period.invalid === "periodFrom"}
													 onChange={value => this.update({periodFrom: value})}/>
							</label>
						</div>
					</Col>
					<Col xl={6} lg={6} md={6}>
						<div className={"m-form-group"} style={styles.dateGroup} ref={el => this.periodToField = el}>
							<label style={{display: "block"}}>
								<span className={"m-form-group_label"}>Period to</span>
								{/* The calendar offers only the rest of the start's month: a period may not cross into the next one. */}
								<EvChargingDateField key={`to-${resetKey}`} value={periodTo}
													 invalid={period.invalid === "periodTo"}
													 openToDate={validFrom}
													 minDate={validFrom}
													 maxDate={validFrom && moment(validFrom).endOf("month")}
													 onChange={value => this.update({periodTo: value})}/>
							</label>
						</div>
					</Col>
				</Row>
				{/* One line for the whole period, always there, so the form below never moves. */}
				<div aria-live={"polite"} style={styles.periodLine}>
					<span style={periodLineStyles[period.kind]}>{period.text}</span>
				</div>
				<div className={"m-form-group"}>
					<label className={"m-form-group_label"} htmlFor={"ev-kwh"}>Units used (kWh)</label>
					<input id={"ev-kwh"} className={"a-input"} inputMode={"decimal"} value={kwh} placeholder={"1,200"}
						   aria-describedby={"ev-kwh-messages"} aria-invalid={parsedKwh.kind === "invalid"}
						   style={{borderColor: parsedKwh.kind === "invalid" ? evColors.error : undefined}}
						   onChange={e => this.update({kwh: e.target.value})}/>
					<div id={"ev-kwh-messages"} aria-live={"polite"}>
						{parsedKwh.kind === "invalid" && <div style={evMessageStyles.error}>{parsedKwh.message}</div>}
						{this.renderMessages("kwh", problems, "error")}
					</div>
				</div>
			</>
		);
	}

	private renderSummary(problems: EvChargingBillMessage[]) {
		const {check, checking, checkFailed, saving, saveError} = this.state;
		const missing = this.missing(problems);
		const credits = check && check.credits !== undefined ? formatCredits(check.credits) : undefined;
		const quarter = check && check.year && check.quarter ? quarterLabel(check.year, check.quarter) : undefined;
		return (
			<>
				<div style={{...styles.summary, opacity: checking ? 0.6 : 1}} aria-live={"polite"}>
					{credits
						? <span>This job earns <strong>{credits} credits</strong></span>
						: <span style={{color: evColors.muted}}>Credits show once the form is complete</span>}
					{quarter && <span> · goes into <strong>{quarter}</strong></span>}
					{check && check.submissionDeadline && <span> · NORA deadline {formatDate(check.submissionDeadline)}</span>}
				</div>
				{checkFailed &&
				<div style={evMessageStyles.hint}>Couldn’t check just now — it will be checked again when you save.</div>}
				{missing.length > 0 && <div style={evMessageStyles.hint}>Still needed: {missing.join(", ")}.</div>}
				{saveError && <div role={"alert"} style={evMessageStyles.error}>{saveError}</div>}
				<div style={{marginTop: 20}}>
					<FormButtonsView align={"right"}>
						<span style={{marginRight: 12, display: "inline-block"}}>
							<ButtonSecondary label={"Cancel"} onClick={this.cancel}/>
						</span>
						<ButtonPrimary label={"Create job"} onClick={this.save} isLoading={saving}
									   disabled={!this.canSave()}/>
					</FormButtonsView>
				</div>
			</>
		);
	}

	private renderSaved(saved: Saved) {
		const {job} = saved;
		return (
			<SectionView>
				<h4 style={{marginTop: 0}}>Job created</h4>
				<p>
					{formatCredits(job.credits)} credits added to {quarterLabel(job.year, job.quarter)} for{" "}
					<strong>{saved.partnerBrandName}</strong> · meter {formatMprn(saved.mprn)} ·{" "}
					{formatDate(saved.periodFrom)} – {formatDate(saved.periodTo)} · {formatKwh(saved.kwh)} kWh ·{" "}
					<span style={{color: evColors.muted}}>
						{saved.fileNames.length > 2 ? `${saved.fileNames.length} files` : saved.fileNames.join(", ")}
					</span>
				</p>
				{job.warnings.map(w => <div key={w.code} style={evMessageStyles.warning}>{w.message}</div>)}
				<div ref={el => this.savedActions = el}
					 style={{display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 20}}>
					<ButtonPrimary label={"Add next job for this meter"} onClick={this.nextBillForSameMeter}/>
					<ButtonSecondary label={"Add a job for another meter"} onClick={this.anotherBill}/>
					<ButtonSecondary label={"View job"} onClick={() => this.props.history.push(Routes.EditJob(job.id))}/>
				</div>
				<div style={evMessageStyles.hint}>
					The next period for this meter starts on {formatDate(nextPeriodFrom(saved.periodTo))}.
				</div>
			</SectionView>
		);
	}

	private renderMessages(field: string, messages: EvChargingBillMessage[], kind: "error" | "warning") {
		return messagesFor(field, messages).map(m => <div key={m.code} style={evMessageStyles[kind]}>{m.message}</div>);
	}

	/**
	 * The one thing worth saying about the period now, in a few words: what stops it first, then what is worth a look,
	 * then its length. Before both dates are in, the rule they have to keep.
	 */
	private periodMessage(problems: EvChargingBillMessage[], existing?: EvChargingMeterPoint): PeriodMessage {
		const {check, periodFrom, periodTo} = this.state;
		const noSuchDate = (field: "periodFrom" | "periodTo", value: Option<moment.Moment>) =>
			value.filter(m => !m.isValid())
				.map(m => ({kind: "error", invalid: field, text: `No such date: ${String(m.creationData().input).slice(0, 5)}`}))
				.orNull as PeriodMessage | null;
		const invalidDate = noSuchDate("periodFrom", periodFrom) || noSuchDate("periodTo", periodTo);
		if (invalidDate) return invalidDate;

		// the backend says the same, but only once a brand and a meter are in; these need nothing but the dates
		const from = isoDate(periodFrom);
		const to = isoDate(periodTo);
		if (from && to && from > to) return {kind: "error", invalid: "periodTo", text: "\"Period to\" is before \"Period from\""};
		if (from && to && from.slice(0, 7) !== to.slice(0, 7))
			return {kind: "error", invalid: "periodTo", text: "Both dates must be in one month"};

		const problem = problems.filter(p => p.field === "periodFrom" || p.field === "periodTo")[0];
		if (problem) return {kind: "error", invalid: problem.field as PeriodField, text: shortPeriodProblem(problem)};

		const warnings = check && this.checkedFor("periodTo") ? check.warnings : [];
		if (warnings.length) {
			const late = check && check.year && check.quarter && check.submissionDeadline;
			return {
				kind: "warning",
				text: late && warnings[0].code === "SUBMISSION_WINDOW_CLOSED"
					? `Past the ${quarterLabel(check!.year!, check!.quarter!)} NORA deadline (${formatDate(check!.submissionDeadline!)})`
					: warnings[0].message
			};
		}
		if (existing && existing.lastBillPeriodTo && from && from > nextPeriodFrom(existing.lastBillPeriodTo))
			return {kind: "warning", text: `Last job ended ${formatDate(existing.lastBillPeriodTo)} - one missing?`};

		const days = periodDays(from, to);
		if (days) return {kind: "hint", text: `${days} ${days === 1 ? "day" : "days"}`};
		return {kind: "hint", text: "Within one calendar month"};
	}

	/**
	 * Problems worth showing now: those on fields that still read as when they were checked. The MPRN's length is
	 * left to the digit counter until all 11 digits are in, rather than flagged while it is still being typed.
	 */
	private shownProblems(): EvChargingBillMessage[] {
		const {check, mprn} = this.state;
		const problems = check ? check.problems.filter(p => this.checkedFor(p.field)) : [];
		return mprn.length < MPRN_LENGTH ? problems.filter(p => p.code !== "MPRN_FORMAT") : problems;
	}

	/**
	 * Whether the check on screen still speaks for the field. The period's verdict depends on the meter too.
	 */
	private checkedFor(field: string): boolean {
		const {checkedInput, mprn, periodFrom, periodTo, kwh} = this.state;
		if (!checkedInput) return false;
		const sameMprn = checkedInput.mprn === mprn;
		switch (field) {
			case "mprn":
				return sameMprn;
			case "periodFrom":
			case "periodTo":
				return sameMprn && checkedInput.periodFrom === isoDate(periodFrom)
					&& checkedInput.periodTo === isoDate(periodTo);
			case "kwh":
				return checkedInput.kwh === kwh;
			default:
				return true;
		}
	}

	private createsMeterPoint(): boolean {
		const {check, mprn} = this.state;
		return !!check && check.createsMeterPoint && mprn.length === MPRN_LENGTH && this.checkedFor("mprn");
	}

	/**
	 * What still stops the bill being saved, in words, so a disabled button never leaves anyone guessing. Problems the
	 * backend found show on their own fields.
	 */
	private missing(problems: EvChargingBillMessage[]): string[] {
		const {partnerBrand, mprn, periodFrom, periodTo, kwh, files, newMeterConfirmed} = this.state;
		const missing: string[] = [];
		if (partnerBrand.isEmpty) missing.push("partner brand");
		if (mprn.length !== MPRN_LENGTH) missing.push("MPRN");
		else if (this.createsMeterPoint() && !newMeterConfirmed) missing.push("confirming the new meter");
		if (!isoDate(periodFrom) || !isoDate(periodTo)) missing.push("billing period");
		if (parseKwh(kwh).kind !== "ok") missing.push("units used");
		if (!files.length) missing.push("evidence");
		// the backend's limit
		else if (files.length > MAX_FILES) missing.push(`at most ${MAX_FILES} files`);
		if (problems.length) missing.push("fixing the errors above");
		return missing;
	}

	/**
	 * Only once the check on screen matches what was typed: an older one could let a new meter through unconfirmed.
	 * When checking failed, saving is left to the backend, which applies the same rules.
	 */
	private canSave(): boolean {
		return !this.state.saving && !this.state.checking && this.missing(this.shownProblems()).length === 0;
	}

	private submitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const target = event.target as HTMLElement;
		// pickers that use Enter themselves prevent it first
		if (event.key === "Enter" && !event.defaultPrevented && target.tagName === "INPUT" && this.canSave())
			this.save();
	};

	private onPartnerBrandSelected = (partnerBrand: Option<PartnerBrandSearch>) => {
		// an MPRN typed for another brand would be checked against this one
		this.update({partnerBrand, meterPoints: [], mprn: "", newMeterConfirmed: false});
		partnerBrand.forEach(pb => this.loadMeterPoints(pb.id));
	};

	private loadMeterPoints(partnerBrandId: string) {
		this.setState({meterPointsLoading: true});
		api.instance.evChargingApi.getMeterPoints(partnerBrandId)
			.then(meterPoints => {
				// the brand may have been changed while the list was on its way
				if (this.state.partnerBrand.exists(pb => pb.id === partnerBrandId))
					this.setState({meterPoints, meterPointsLoading: false});
			})
			.catch(error => {
				this.setState({meterPointsLoading: false});
				notifyBugsnagAndShowToast(error, "Fetch meters");
			});
	}

	private onFilesAdded = (added: File[]) => {
		const files = added.map(file => ({key: `${++this.fileKeys}`, file, url: URL.createObjectURL(file)}));
		// the first of the new ones is shown, as the one most likely being looked for
		this.setState(state => ({files: [...state.files, ...files], selectedFileKey: files[0].key}));
	};

	private onFileRemoved = (key: string) => {
		releaseFiles(this.state.files.filter(f => f.key === key));
		this.setState(state => {
			const files = state.files.filter(f => f.key !== key);
			const selectedFileKey = state.selectedFileKey === key ? (files[0] && files[0].key) : state.selectedFileKey;
			return {files, selectedFileKey};
		});
	};

	/**
	 * Uploads the files not uploaded yet, a few at a time, keeping each id as it comes so a retry after a failure
	 * uploads only the rest. Ids come back in the order of the files.
	 */
	private uploadFiles(): Promise<string[]> {
		const files = this.state.files;
		const ids: string[] = new Array(files.length);
		let next = 0;
		const worker = (): Promise<void> => {
			const index = next++;
			if (index >= files.length) return Promise.resolve();
			const evidence = files[index];
			if (evidence.attachmentId) {
				ids[index] = evidence.attachmentId;
				return worker();
			}
			return api.instance.attachmentApi.uploadFile(evidence.file).then(attachmentId => {
				ids[index] = attachmentId;
				this.setState(state => ({
					files: state.files.map(f => f.key === evidence.key ? {...f, attachmentId} : f)
				}));
				return worker();
			});
		};
		const workers: Promise<void>[] = [];
		for (let i = 0; i < Math.min(PARALLEL_UPLOADS, files.length); i++) workers.push(worker());
		return Promise.all(workers).then(() => ids);
	}

	/**
	 * Applies a change to the bill and checks it again once typing pauses. Until then the check on screen is marked
	 * out of date, which keeps Create disabled.
	 */
	private update(change: Partial<State>) {
		this.setState({...change, checking: true} as State, this.scheduleCheck);
	}

	private scheduleCheck = () => {
		window.clearTimeout(this.checkTimer);
		this.checkTimer = window.setTimeout(this.runCheck, CHECK_DELAY_MS);
	};

	private runCheck = () => {
		const {partnerBrand, mprn, periodFrom, periodTo, kwh} = this.state;
		const sequence = ++this.checkSequence;
		if (partnerBrand.isEmpty) {
			this.setState({check: undefined, checkedInput: undefined, checking: false, checkFailed: false});
			return;
		}
		const parsedKwh = parseKwh(kwh);
		const checkedInput: CheckedInput = {mprn, periodFrom: isoDate(periodFrom), periodTo: isoDate(periodTo), kwh};
		api.instance.evChargingApi.checkBill({
			partnerBrandId: partnerBrand.get.id,
			mprn,
			periodFrom: checkedInput.periodFrom,
			periodTo: checkedInput.periodTo,
			kwh: parsedKwh.kind === "ok" ? parsedKwh.value : undefined
		})
			.then(check => {
				// an answer to an older version of the bill must not overwrite the newer one
				if (sequence === this.checkSequence)
					this.setState({check, checkedInput, checking: false, checkFailed: false});
			})
			.catch(error => {
				if (sequence !== this.checkSequence) return;
				// the last answer stays: its messages still hold for the fields that have not changed since
				this.setState({checking: false, checkFailed: true});
				// reported once, not on every pause in typing
				if (!this.checkFailureReported) {
					this.checkFailureReported = true;
					notifyBugsnagAndShowToast(error, "Check bill");
				}
			});
	};

	private save = () => {
		const {partnerBrand, mprn, periodFrom, periodTo, kwh, files} = this.state;
		const parsedKwh = parseKwh(kwh);
		if (!this.canSave() || !files.length || partnerBrand.isEmpty || parsedKwh.kind !== "ok") return;
		this.setState({saving: true, saveError: undefined});
		this.uploadFiles()
			.then(attachmentIds => api.instance.evChargingApi.createJob({
				partnerBrandId: partnerBrand.get.id,
				mprn,
				periodFrom: isoDate(periodFrom)!,
				periodTo: isoDate(periodTo)!,
				kwh: parsedKwh.value,
				billAttachmentIds: attachmentIds
			}))
			.then(job => this.setState({
				saving: false,
				saved: {
					job,
					partnerBrandName: partnerBrand.get.name,
					mprn,
					periodFrom: isoDate(periodFrom)!,
					periodTo: isoDate(periodTo)!,
					kwh: parsedKwh.value,
					fileNames: files.map(f => f.file.name)
				}
			}, () => this.focusIn(this.savedActions)))
			.catch(error => {
				const data = error && error.response && error.response.data;
				// a 422 names what to fix; anything else is unexpected and reported
				if (error && error.response && error.response.status === 422 && data && data.message)
					this.setState({saving: false, saveError: data.message});
				else {
					this.setState({saving: false, saveError: "The job could not be saved. Try again."});
					notifyBugsnagAndShowToast(error, "Create EV charging job");
				}
			});
	};

	private cancel = () => {
		const {files, mprn, kwh} = this.state;
		if ((files.length || mprn || kwh) && !window.confirm("Discard this job? What you entered will be lost.")) return;
		this.props.history.push(Routes.JobsList);
	};

	/**
	 * Clients send bills in series, so the most common next step is the following bill of the same meter.
	 */
	private nextBillForSameMeter = () => {
		const {saved} = this.state;
		if (!saved) return;
		releaseFiles(this.state.files);
		this.setState({
			saved: undefined,
			check: undefined,
			checkedInput: undefined,
			newMeterConfirmed: true,
			periodFrom: option(moment(nextPeriodFrom(saved.periodTo), ISO_DATE, true)),
			periodTo: none,
			kwh: "",
			files: [],
			selectedFileKey: undefined,
			saveError: undefined,
			resetKey: this.state.resetKey + 1
		} as State, () => {
			this.scheduleCheck();
			this.focusIn(this.periodToField);
		});
		this.state.partnerBrand.forEach(pb => this.loadMeterPoints(pb.id));
	};

	private anotherBill = () => {
		releaseFiles(this.state.files);
		const {partnerBrand, resetKey} = this.state;
		this.setState({...initialState, partnerBrand, resetKey: resetKey + 1}, () => {
			this.scheduleCheck();
			const mprnInput = document.getElementById("ev-mprn");
			if (mprnInput) mprnInput.focus();
		});
		partnerBrand.forEach(pb => this.loadMeterPoints(pb.id));
	};

	private focusIn(container: HTMLElement | null) {
		const target = container && container.querySelector("input, button") as HTMLElement | null;
		if (target) target.focus();
	}
}

// RestrictedRoute renders the page without the router's props
export default withRouter(EvChargingBillPage);

/**
 * The date as the backend wants it, or nothing while it is missing or does not exist (31/02).
 */
function isoDate(value: Option<moment.Moment>): string | undefined {
	return value.filter(m => m.isValid()).map(m => m.format(ISO_DATE)).orNull || undefined;
}

type PeriodField = "periodFrom" | "periodTo";

interface PeriodMessage {
	kind: "error" | "warning" | "hint";
	text: string;
	/**
	 * The date field the message is about, outlined in red.
	 */
	invalid?: PeriodField;
}

/**
 * The backend explains a refused period in full sentences, fit for the job note; under the dates a few words do.
 * The overlapping job and its dates are read out of the sentence, which is all the check returns; a message that
 * does not read as expected shows whole.
 */
function shortPeriodProblem(problem: EvChargingBillMessage): string {
	switch (problem.code) {
		case "PERIOD_REVERSED":
			return "\"Period to\" is before \"Period from\"";
		case "PERIOD_CROSSES_MONTH":
			return "Both dates must be in one month";
		case "PERIOD_OVERLAPS_CLAIMED_BILL": {
			const overlap = /job (\S+) for (.+) to (.+)\. /.exec(problem.message);
			return overlap ? `Overlaps job ${overlap[1]} (${overlap[2]} – ${overlap[3]})` : problem.message;
		}
		default:
			return problem.message;
	}
}

function releaseFiles(files: EvidenceFile[]) {
	files.forEach(f => URL.revokeObjectURL(f.url));
}

const periodLineStyles: { [kind: string]: React.CSSProperties } = {
	error: {color: evColors.error},
	warning: {background: evColors.note, padding: "1px 6px", borderRadius: 4},
	hint: {color: evColors.muted}
};

const styles: { [key: string]: React.CSSProperties } = {
	// the line under the dates takes the gap the fields leave below them
	dateGroup: {marginBottom: 6},
	periodLine: {minHeight: 17, marginBottom: 24, fontSize: 12, lineHeight: "17px", color: evColors.navy},
	summary: {
		background: evColors.background, borderRadius: 4, padding: "12px 14px", fontSize: 14, lineHeight: "20px",
		color: evColors.navy
	}
};
